import type { CrmContactAddress } from "@/lib/crmContactAddresses";
import {
  channelsStateFromContact,
  type CrmContactChannelsState,
  type CrmContactEmail,
  type CrmContactPhone,
  type CrmContactSocial,
} from "@/lib/crmContactChannels";
import {
  parseLeadSourceDetailChannelOverflow,
  type LeadSourceDetailChannelBucket,
} from "@/lib/crmLeadSourceDetailChannels";

export type LeadLinkedAccountSnapshot = {
  id: string;
  name?: string | null;
  domain?: string | null;
  website?: string | null;
  industry?: string | null;
  industrySegment?: string | null;
  email?: string | null;
  companyPhone?: string | null;
  status?: string | null;
  addresses?: CrmContactAddress[];
};

export type LeadDetailContactSnapshot = {
  contactId?: string | null;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  title?: string | null;
  linkedinUrl?: string | null;
  phone?: string | null;
};

export type LeadDetailFetchedContact = LeadDetailContactSnapshot & {
  id?: string;
  emails?: CrmContactEmail[];
  phones?: CrmContactPhone[];
  socials?: CrmContactSocial[];
  addresses?: CrmContactAddress[];
};

export type LeadDetailCompanyView = {
  name: string | null;
  domain: string | null;
  website: string | null;
  industry: string | null;
  industrySegment: string | null;
  channels: CrmContactChannelsState;
};

function bucketToContactShape(
  snapshot: LeadDetailContactSnapshot,
  bucket?: LeadSourceDetailChannelBucket | null,
) {
  return {
    firstName: snapshot.firstName,
    lastName: snapshot.lastName,
    email: snapshot.email,
    phone: snapshot.phone,
    linkedinUrl: snapshot.linkedinUrl,
    emails: bucket?.emails,
    phones: bucket?.phones,
    socials: bucket?.socials,
    addresses: bucket?.addresses,
  };
}

export function buildLeadCompanyDetailView(input: {
  lead: {
    companyName?: string | null;
    companyDomain?: string | null;
    companyWebsite?: string | null;
    companyIndustry?: string | null;
    sourceDetail?: string | null;
    leadType?: string | null;
  };
  account: LeadLinkedAccountSnapshot | null;
}): LeadDetailCompanyView | null {
  const { lead, account } = input;
  const overflow = parseLeadSourceDetailChannelOverflow(lead.sourceDetail);
  const hasCompany =
    Boolean(lead.companyName?.trim()) ||
    Boolean(account?.name?.trim()) ||
    lead.leadType === "company_only" ||
    lead.leadType === "company_plus_contact";

  if (!hasCompany) return null;

  const companyBucket = overflow?.company;
  const channels = channelsStateFromContact({
    email: account?.email ?? undefined,
    phone: account?.companyPhone ?? undefined,
    emails: companyBucket?.emails,
    phones: companyBucket?.phones,
    socials: companyBucket?.socials,
    addresses: account?.addresses?.length ? account.addresses : companyBucket?.addresses,
  });

  return {
    name: account?.name?.trim() || lead.companyName?.trim() || null,
    domain: account?.domain?.trim() || lead.companyDomain?.trim() || null,
    website: account?.website?.trim() || lead.companyWebsite?.trim() || null,
    industry: account?.industry?.trim() || lead.companyIndustry?.trim() || null,
    industrySegment: account?.industrySegment?.trim() || null,
    channels,
  };
}

export function buildLeadContactChannelsView(input: {
  snapshot: LeadDetailContactSnapshot;
  index: number;
  fetched: LeadDetailFetchedContact | null;
  sourceDetail?: string | null;
  leadType?: string | null;
}): CrmContactChannelsState {
  const overflow = parseLeadSourceDetailChannelOverflow(input.sourceDetail);
  if (input.fetched) {
    return channelsStateFromContact(input.fetched);
  }

  const isAccountLead =
    input.leadType === "company_only" || input.leadType === "company_plus_contact";
  const bucket = isAccountLead
    ? overflow?.attachedContacts?.[input.index]
    : overflow?.contact;

  return channelsStateFromContact(bucketToContactShape(input.snapshot, bucket));
}

export function leadContactDisplayName(snapshot: LeadDetailContactSnapshot): string {
  return (
    snapshot.fullName?.trim() ||
    [snapshot.firstName, snapshot.lastName].filter(Boolean).join(" ").trim() ||
    snapshot.email?.trim() ||
    "Unknown contact"
  );
}

export function leadContactSummaryLine(channels: CrmContactChannelsState): string {
  const primaryEmail = channels.emails.find((e) => e.isPrimary && e.address.trim())?.address.trim();
  const anyEmail = channels.emails.find((e) => e.address.trim())?.address.trim();
  return primaryEmail || anyEmail || "No email";
}
